import React, { useMemo, useState } from 'react';
import { BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, fmtDate, panelPost } from '../kit';
import { Pill, num, rowsOf, str } from '../ipintel/shared';
import { type HzCtx, S, fmtCost, fmtN, horizonLabel, pct, promptLabel, shortId } from './shared';

/**
 * Overview — GET horizon/overview: the headline numbers, which universities'
 * technologies have Horizon data and which are pending, the spread of the
 * current data by horizon / industry / value lever (each bar filters the
 * Technologies tab), results per prompt and per run, and the source in use —
 * the prompt priority that decides each technology's current result, with
 * the rebuild of that current data (POST horizon/refresh_current).
 */

type Res = { data: any; loading: boolean; error: string; reload: () => void };

const COV_SORTS: Record<string, string> = {
  pending: 'Most pending first', pct: 'Lowest coverage first', techs: 'Largest first', name: 'By name',
};

const HzOverview: React.FC<{ res: Res; ctx: HzCtx }> = ({ res, ctx }) => {
  const d = res.data || {};
  const k = d.kpis || {};
  const src = d.source || {};
  const priority = rowsOf(src.prompt_priority);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [covSort, setCovSort] = useState('pending');
  const [onlyPending, setOnlyPending] = useState(false);

  const coverage = useMemo(() => {
    let rows = rowsOf(d.coverage_by_university).map((u: any) => ({
      ...u, code: str(u.code), label: str(u.label) || str(u.name) || str(u.code),
      techs: num(u.techs), with_data: num(u.with_data), pending: num(u.pending),
      pct_num: num(u.techs) ? num(u.with_data) / num(u.techs) : 0,
    }));
    if (onlyPending) rows = rows.filter((u: any) => u.pending > 0);
    const by: Record<string, (a: any, b: any) => number> = {
      pending: (a, b) => b.pending - a.pending || b.techs - a.techs,
      pct: (a, b) => a.pct_num - b.pct_num || b.pending - a.pending,
      techs: (a, b) => b.techs - a.techs,
      name: (a, b) => a.label.localeCompare(b.label),
    };
    return rows.sort(by[covSort] || by.pending);
  }, [d.coverage_by_university, covSort, onlyPending]);

  const byHorizon = rowsOf(d.by_horizon).map((x: any) => ({ key: str(x?.horizon), label: horizonLabel(x?.horizon), value: num(x?.n) }))
    .filter((x: any) => x.key);
  const byIndustry = rowsOf(d.by_industry).map((x: any) => ({ label: str(x?.industry) || '(none)', value: num(x?.n) }))
    .sort((a: any, b: any) => b.value - a.value);
  const byLever = rowsOf(d.by_lever).map((x: any) => ({ label: str(x?.lever), value: num(x?.n) }))
    .filter((x: any) => x.label).sort((a: any, b: any) => b.value - a.value);
  const byConf = rowsOf(d.by_confidence).map((x: any) => ({ label: str(x?.confidence), value: num(x?.n) }))
    .filter((x: any) => x.label);
  // Top technical tags, labelled by the tag (each belongs to exactly one lever), filtering by it.
  const byTag = rowsOf(d.by_tag).map((x: any) => ({ label: str(x?.tag), tag: str(x?.tag), value: num(x?.n) }))
    .filter((x: any) => x.tag).slice(0, 25);
  const byPrompt = rowsOf(d.by_prompt);
  const byRun = rowsOf(d.by_run);

  const refresh = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelPost(S, 'refresh_current');
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: `Refresh failed: ${error}` }); return; }
    // {refresh: {rows, by_prompt: [{id, name, n}], took_ms} | {skipped}, source}
    const rf = data?.refresh && typeof data.refresh === 'object' ? data.refresh : (data || {});
    if (rf.skipped) { setMsg({ kind: 'error', text: `Not rebuilt: ${str(rf.skipped)}` }); ctx.reloadAll(); return; }
    const parts = rowsOf(rf.by_prompt).map((x: any) => `${num(x?.n).toLocaleString()} from ${str(x?.name) || shortId(x?.id)} (${shortId(x?.id)})`);
    setMsg({
      kind: 'ok',
      text: `Current data rebuilt: ${num(rf.rows).toLocaleString()} technologies${rf.took_ms !== undefined ? ` in ${(num(rf.took_ms) / 1000).toFixed(1)} s` : ''}${parts.length ? ` — ${parts.join(' · ')}` : ''}.`,
    });
    ctx.reloadAll();
  };

  if (res.loading && !res.data) return <Loading label="Counting Horizon results…" />;
  return (
    <div className="flex flex-col gap-4 min-w-0">
      {res.error && <Msg kind="error">{res.error}</Msg>}
      <KPIRow>
        <KPI label="Technologies" value={fmtN(k.technologies)} hint="in the corpus · click to list"
          onClick={() => ctx.goTech({ status: 'all' })} />
        <KPI label="With Horizon data" value={fmtN(k.with_data)} hint={`${pct(k.with_data, k.technologies)} · click to list`}
          onClick={() => ctx.goTech({ status: 'has' })} />
        <KPI label="Pending" value={fmtN(k.pending)}
          hint={`${pct(k.pending, k.technologies)} · no usable result${num(k.pending_no_summary) ? ` · ${fmtN(k.pending_no_summary)} lack a summary` : ''}`}
          onClick={() => ctx.goTab('Pending')} />
        <KPI label="Results" value={fmtN(k.results)} hint="every sandbox result · click to list"
          onClick={() => ctx.goResults({})} />
        <KPI label="Prompts" value={fmtN(k.prompts)} hint="saved prompt versions" onClick={() => ctx.goTab('Prompts')} />
        <KPI label="Runs" value={fmtN(k.runs)} hint="sandbox + batch runs" onClick={() => ctx.goTab('Runs')} />
        <KPI label="Cost" value={fmtCost(k.total_cost)} hint={`${fmtN(k.total_tokens)} tokens`} />
        <KPI label="Errors" value={fmtN(k.errors)} hint={`${fmtN(k.reviewed)} reviewed by hand`}
          onClick={num(k.errors) ? () => ctx.goResults({ error: '1' }) : undefined} />
      </KPIRow>

      <Section title="Source the public tech pages use"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <Btn small kind="primary" onClick={refresh} disabled={busy}
              title="Rebuild horizon_current (one row per technology) from the results now">
              {busy ? 'Rebuilding…' : 'Refresh current data'}
            </Btn>
            <Btn small onClick={() => ctx.goTab('Prompts')} title="Reorder, include or exclude prompts">Edit priority</Btn>
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Each technology's current Horizon data is its newest usable result (no error, a horizon) under the first prompt
          below that has one. A technology the first prompt never covered falls back to the next — that is how missing
          values are filled. The public pages' "Problem &amp; applications" and "Value levers" sections read this current
          data; it is rebuilt daily, after a priority change, and from the button above.
        </p>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {d.prompt_files_match_final === false && (
          <div className="text-xs rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">
            The prompt files in Backend/horizon/prompts/ are not the FINAL prompt's texts, so a run on the pending
            technologies would use a different prompt.{' '}
            <button type="button" className="underline" onClick={() => ctx.goTab('Prompts')}>Compare in Prompts →</button>
          </div>
        )}
        {src.is_default && (
          <div className="text-[11px] text-grayscale-1100">Default order (the three full-catalogue prompts, final first) — no priority saved yet. Newer prompts are used only once added under Prompts.</div>
        )}
        {priority.length ? (
          <ol className="flex flex-col gap-1.5">
            {priority.map((p: any, i: number) => (
              <li key={str(p?.id) || i} className="flex items-start gap-2 text-sm min-w-0">
                <span className="w-6 shrink-0 text-right font-bold text-grayscale-1100 tabular-nums">{i + 1}.</span>
                <span className="min-w-0 flex-1 flex flex-col gap-0.5">
                  <span className="flex items-center gap-2 flex-wrap">
                    <button type="button" onClick={() => ctx.goPrompt(str(p?.id))}
                      className="text-left font-medium text-grayscale-1300 underline underline-offset-2 break-words">
                      {str(p?.name) || shortId(p?.id)}
                    </button>
                    {i === 0 ? <Pill tone="good" title="The prompt finally used — first in the priority">FINAL</Pill>
                      : <Pill tone="neutral" title="Used only where the prompts above have no usable result">fallback</Pill>}
                  </span>
                  <span className="text-[11px] text-grayscale-1100">
                    <span className="font-mono">{shortId(p?.id)}</span>
                    {p?.created_at ? ` · ${fmtDate(p.created_at)}` : ''}
                    {` · ${fmtN(p?.results)} results · ${fmtN(p?.techs)} technologies`}
                    {p?.current_rows !== undefined && <> · <span className="font-bold text-grayscale-1300">{fmtN(p.current_rows)}</span> shown on pages</>}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        ) : <div className="text-sm text-grayscale-1100">No prompt priority set — the server uses its default.</div>}
        <div className="text-xs text-grayscale-1100">
          Current data: <span className="font-bold text-grayscale-1300">{fmtN(src.current_rows)}</span> technologies
          {' '}· rebuilt {src.current_refreshed_at ? fmtDate(src.current_refreshed_at) : 'never'}
        </div>
      </Section>

      <Section title="Coverage by university"
        right={
          <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1200">
            <label className="inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} />
              Only with pending
            </label>
            <Select value={covSort} onChange={setCovSort} options={Object.keys(COV_SORTS)} labels={COV_SORTS} />
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Green = has Horizon data, amber = pending. Click a university for its technologies, or its pending count
          for what still needs a run.
        </p>
        {coverage.length ? (
          <div className="flex flex-col gap-1.5">
            {coverage.map((u: any) => (
              <div key={u.code} className="flex items-center gap-2 text-sm min-w-0">
                <button type="button" onClick={() => ctx.goTech({ university: u.code })}
                  title={`${u.name || u.label} (${u.code}) — list its technologies${num(u.pending_no_summary) ? ` · ${num(u.pending_no_summary)} pending lack a summary` : ''}`}
                  className="w-24 sm:w-40 shrink-0 truncate text-left text-grayscale-1200 hover:underline">
                  {u.label}
                </button>
                <div className="flex-1 min-w-0 h-3 bg-grayscale-200 rounded-full overflow-hidden flex"
                  title={`${u.with_data.toLocaleString()} with data · ${u.pending.toLocaleString()} pending of ${u.techs.toLocaleString()}`}>
                  <div className="h-3 bg-green-600" style={{ width: `${u.techs ? (u.with_data / u.techs) * 100 : 0}%` }} />
                  <div className="h-3 bg-amber-400" style={{ width: `${u.techs ? (u.pending / u.techs) * 100 : 0}%` }} />
                </div>
                <span className="w-24 sm:w-32 shrink-0 text-right text-xs text-grayscale-1300 tabular-nums">
                  {u.with_data.toLocaleString()}/{u.techs.toLocaleString()}
                  <span className="text-grayscale-1100"> · {pct(u.with_data, u.techs)}</span>
                </span>
                <span className="w-16 sm:w-20 shrink-0 text-right">
                  {u.pending > 0 ? (
                    <button type="button" onClick={() => ctx.goPending({ university: u.code })}
                      title="Open the pending technologies of this university"
                      className="text-[11px] rounded-full border px-1.5 py-0.5 whitespace-nowrap text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100">
                      {u.pending.toLocaleString()} pending
                    </button>
                  ) : <span className="text-[11px] text-green-700">complete</span>}
                </span>
              </div>
            ))}
          </div>
        ) : <div className="text-sm text-grayscale-1100">No universities{onlyPending ? ' with pending technologies' : ''}.</div>}
        <DataTable rows={coverage} csvName="horizon_coverage" maxHeight="24rem"
          columns={['label', 'code', 'techs', 'with_data', 'pending', 'pct_num']}
          labels={{ label: 'University', code: 'Code', techs: 'Technologies', with_data: 'With data', pending: 'Pending', pct_num: 'Coverage' }}
          render={{
            label: r => (
              <button type="button" onClick={() => ctx.goTech({ university: r.code })} title={r.name || r.label}
                className="text-left underline underline-offset-2">{r.label}</button>
            ),
            code: r => <span className="font-mono text-xs">{r.code}</span>,
            techs: r => <span className="tabular-nums">{fmtN(r.techs)}</span>,
            with_data: r => <span className="tabular-nums">{fmtN(r.with_data)}</span>,
            pending: r => <span className={`tabular-nums ${r.pending ? 'text-amber-700 font-medium' : ''}`}>{fmtN(r.pending)}</span>,
            pct_num: r => <span className="tabular-nums">{pct(r.with_data, r.techs)}</span>,
          }}
          actions={r => (
            <span className="inline-flex gap-1.5">
              <Btn small onClick={() => ctx.goTech({ university: r.code })}>Technologies</Btn>
              {r.pending > 0 && <Btn small onClick={() => ctx.goPending({ university: r.code })}>Pending</Btn>}
            </span>
          )} />
      </Section>

      <div className="grid lg:grid-cols-3 gap-4 min-w-0">
        <Section title="Current data by horizon">
          {byHorizon.length
            ? <BarList items={byHorizon} onSelect={l => { const h = byHorizon.find((x: any) => x.label === l); if (h) ctx.goTech({ horizon: h.key, status: 'has' }); }} />
            : <div className="text-sm text-grayscale-1100">No current data yet.</div>}
          <div className="text-[11px] text-grayscale-1100">Click a bar to list those technologies.</div>
        </Section>
        <Section title="Current data by industry">
          {byIndustry.length
            ? <BarList items={byIndustry} onSelect={l => ctx.goTech({ industry: l === '(none)' ? '' : l, status: 'has' })} />
            : <div className="text-sm text-grayscale-1100">No current data yet.</div>}
        </Section>
        <Section title="Current data by value lever">
          {byLever.length
            ? <BarList items={byLever} onSelect={l => ctx.goTech({ lever: l, status: 'has' })} />
            : <div className="text-sm text-grayscale-1100">No value lever tags yet.</div>}
          <div className="text-[11px] text-grayscale-1100">
            Technologies carrying at least one tag under the lever{num(k.no_levers) ? ` · ${fmtN(k.no_levers)} have no lever tags` : ''}.
          </div>
        </Section>
        <Section title="Current data by confidence">
          {byConf.length
            ? <BarList items={byConf} onSelect={l => ctx.goTech({ confidence: l, status: 'has' })} />
            : <div className="text-sm text-grayscale-1100">No current data yet.</div>}
        </Section>
        <div className="lg:col-span-2 min-w-0">
          <Section title="Top technical tags (current data)">
            {byTag.length
              ? <BarList items={byTag} onSelect={l => { const t = byTag.find((x: any) => x.label === l); if (t) ctx.goTech({ tag: t.tag, status: 'has' }); }} />
              : <div className="text-sm text-grayscale-1100">No technical tags yet.</div>}
          </Section>
        </div>
      </div>

      <div className="flex flex-col gap-4 min-w-0">
        <Section title={`Results by prompt (${byPrompt.length})`}>
          <DataTable rows={byPrompt} maxHeight="20rem" empty="No results yet."
            columns={['name', 'id', 'prompt_id', 'created_at', 'results', 'ok', 'errors', 'techs', 'runs', 'current_rows', 'cost']
              .filter(c => byPrompt.length && c in byPrompt[0])}
            labels={{ name: 'Prompt', id: 'Id', prompt_id: 'Id', created_at: 'Created', results: 'Results', ok: 'Usable',
              errors: 'Errors', techs: 'Technologies', runs: 'Runs', current_rows: 'Shown on pages', cost: 'Cost' }}
            render={{
              prompt_id: r => <span className="font-mono text-xs">{shortId(r.prompt_id)}</span>,
              id: r => <span className="font-mono text-xs">{shortId(r.id)}</span>,
              name: r => <span className="block max-w-[18rem] truncate" title={str(r.name)}>{str(r.name) || promptLabel(ctx.promptById(r.prompt_id ?? r.id), r.prompt_id ?? r.id)}</span>,
              cost: r => fmtCost(r.cost), total_cost: r => fmtCost(r.total_cost),
            }}
            actions={r => (
              <span className="inline-flex gap-1.5">
                <Btn small onClick={() => ctx.goResults({ prompt: str(r.prompt_id ?? r.id) })}>Results</Btn>
                <Btn small onClick={() => ctx.goPrompt(str(r.prompt_id ?? r.id))}>Prompt</Btn>
              </span>
            )} />
        </Section>
        <Section title={`Results by run (${byRun.length})`}>
          <DataTable rows={byRun} maxHeight="20rem" empty="No runs yet."
            columns={['created_at', 'id', 'run_id', 'prompt_name', 'prompt_id', 'model', 'results', 'ok', 'techs', 'errors', 'current_rows', 'total_cost']
              .filter(c => byRun.length && c in byRun[0])}
            labels={{ created_at: 'Started', id: 'Run', run_id: 'Run', prompt_name: 'Prompt', prompt_id: 'Prompt id', model: 'Model',
              results: 'Results', ok: 'Usable', techs: 'Technologies', errors: 'Errors', current_rows: 'Shown on pages', total_cost: 'Cost' }}
            render={{
              run_id: r => <span className="font-mono text-xs">{shortId(r.run_id)}</span>,
              id: r => <span className="font-mono text-xs">{shortId(r.id)}</span>,
              prompt_id: r => <span className="font-mono text-xs">{shortId(r.prompt_id)}</span>,
              prompt_name: r => <span className="block max-w-[16rem] truncate" title={str(r.prompt_name)}>{str(r.prompt_name) || '—'}</span>,
              cost: r => fmtCost(r.cost), total_cost: r => fmtCost(r.total_cost),
            }}
            actions={r => <Btn small onClick={() => ctx.goResults({ run: str(r.run_id ?? r.id) })}>Results</Btn>} />
        </Section>
      </div>
    </div>
  );
};

export default HzOverview;
